import {
  OrganisationFeatureFlags,
  StatementLinkBehaviorTypes,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from "shared";
import { When } from "react-if";
import { useMemo } from "react";
import { toast } from "react-toastify";

import { Card } from "../../../../../common/Atoms/Card";
import { CardContent } from "../../../../../common/Atoms/CardContent";
import { IOption, Select } from "../../../../../common/Atoms/Select";
import { useGeneralSettingsForm } from "../../../../hooks/useGeneralSettingsForm";
import { fieldIsValid } from "../../../../lib/helper";
import { SaveAndError } from "../SaveAndError";
import { Toggle } from "../../../../../common/Atoms/Toggle";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { useGetStatementBuilderTemplatesQuery, useUpdateStatementBuilderEnabledMutation } from "../../../../services/api/statementBuilderApi/statementBuilder";

/** Settings that apply to every statement, whichever template renders it */
export function StatementCommon() {
  const {
    formData,
    onChange,
    invalids,
  } = useGeneralSettingsForm();

  const currentOrg = useGetSelectedOrganisation();
  const aiStatementBuilderEnabled = !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_STATEMENT_BUILDER);
  // Partial access: can use the builder but can't switch statements to the AI template
  const aiStatementBuilderPartial = !aiStatementBuilderEnabled && !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_STATEMENT_BUILDER_PARTIAL);

  const { data: templatesData } = useGetStatementBuilderTemplatesQuery(
    { organisationId: currentOrg?.id || `` },
    { skip: !currentOrg?.id || !(aiStatementBuilderEnabled || aiStatementBuilderPartial) },
  );
  const [updateBuilderEnabled, updateBuilderEnabledState] = useUpdateStatementBuilderEnabledMutation();
  const templates = templatesData?.templates || [];
  const publishedTemplates = templates.filter(t => t.hasPublished);
  const defaultTemplate = templates.find(t => t.enabled);

  const defaultTemplateOptions: IOption[] = [
    { label: `Standard template`, value: `standard` },
    ...publishedTemplates.map(t => ({ label: t.title, value: String(t.id) })),
  ];

  async function handleDefaultTemplateChange({ value }: { value: string | number | boolean }) {
    const template = templates.find(t => String(t.id) === value);
    const target = template || defaultTemplate;

    // Nothing to switch when picking the current default (or standard with no default)
    if (!currentOrg?.id || !target || template?.enabled) {
      return;
    }

    try {
      await updateBuilderEnabled({
        organisationId: currentOrg.id,
        templateId: target.id,
        enabled: !!template,
      }).unwrap();

      toast.success(template ? `Statements now use ${template.title}.` : `Statements now use the standard template.`);
    }
    catch {
      toast.error(`Could not update statement mode.`);
    }
  }

  const statementLinkOptions: IOption[] = useMemo(() => {
    const baseOptions: IOption[] = [
      {
        label: `Customer Portal`,
        value: StatementLinkBehaviorTypes.PORTAL,
        tooltip: `Recommended for most users.`,
      },
      {
        label: `Disabled`,
        value: StatementLinkBehaviorTypes.DISABLED,
        tooltip: `Do not show invoice links on statement PDFs.`,
      },
    ];

    if (accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_DIRECT_STATEMENT_LINK)) {
      baseOptions.push({
        label: `Direct to ${currentOrg?.accountingSystemName}`,
        value: StatementLinkBehaviorTypes.DIRECT,
        tooltip: `Directly link to the invoice in ${currentOrg?.accountingSystemName}. If the invoice has no shareable ${currentOrg?.accountingSystemName} link use the Paidnice portal instead.`,
      });
    }

    return baseOptions;
  }, [currentOrg?.accountingSystemType]);

  return (
    <Card>
      <CardContent className={ `pt-6` }>
        <div className={ `w-full max-w-[50%] space-y-4` }>
          <When condition={ aiStatementBuilderEnabled || aiStatementBuilderPartial }>
            <Select
              name={ `defaultStatementTemplate` }
              label={ `Default statement template` }
              description={
                aiStatementBuilderPartial
                  ? `Your organisation can design and preview a custom statement in the Custom tab, but using it for customer statements isn't available yet. Customers keep getting the standard template. Contact support if you'd like to start using it.`
                  : publishedTemplates.length > 0
                    ? `Outstanding statements without a policy override use this template. Statement policies can pick a different one. Activity statements always use the standard template.`
                    : `Publish a template in the Custom tab first to switch off the standard template.`
              }
              options={ defaultTemplateOptions }
              selected={ aiStatementBuilderEnabled && defaultTemplate ? String(defaultTemplate.id) : `standard` }
              disabled={ aiStatementBuilderPartial || publishedTemplates.length === 0 || updateBuilderEnabledState.isLoading }
              onChange={ handleDefaultTemplateChange }
            />
          </When>

          <Select
            label={ `Statement Invoice Links` }
            description={ `Control links in the statement PDFs to view the invoice in the Paidnice customer portal.` }
            name={ `statementLinkBehavior` }
            options={ statementLinkOptions }
            selected={ formData.statementLinkBehavior || StatementLinkBehaviorTypes.PORTAL }
            onChange={ v => onChange(`statementLinkBehavior`, v.value) }
            invalid={ fieldIsValid(`statementLinkBehavior`, invalids) }
          />

          <Toggle
            label={ `Always use legal name` }
            description={ `Always use your organisation's legal name, even when "Use Display Name" is enabled.` }
            name={ `statementAlwaysLegalName` }
            checked={ formData.statementAlwaysLegalName || false }
            onChange={ () => onChange(`statementAlwaysLegalName`, !formData.statementAlwaysLegalName) }
          />

          <When condition={ accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_PROJECTS_JOBS) }>
            <Toggle
              label={ `Separate projects & jobs` }
              description={ `When a child contact is flagged as a job or project in ${currentOrg?.accountingSystemName}, separate into its own section on the statement.` }
              name={ `separateJobsProjects` }
              checked={ formData.separateJobsProjects || false }
              onChange={ () => onChange(`separateJobsProjects`, !formData.separateJobsProjects) }
            />
          </When>
        </div>

        <SaveAndError
          className={ `mt-2` }
        />
      </CardContent>
    </Card>
  );
}
